import { http, HttpResponse, delay } from 'msw';
import {
  CreateEmployeeLeaveRequest,
  EmployeeLeaveResponse as EmployeeLeaveResponseDto,
  EmployeeLeaveStatusEnum,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';

import { singleDevice } from '@/mocks/data/deviceData';
import {
  createEmployeeApplicationAccounts,
  employeeIdsWithApps,
  mockEmployeeLeaves,
  offboardingScenarioEmployeesById,
  offboardingScenarios,
  pendingMockEmployees,
  simpleEmployee,
  storefrontEmployee1,
  storefrontEmployee2,
} from '@/mocks/data/employeeData';
import { employeeGroups } from '@/mocks/data/groupData';
import { mockedOffboardings } from '@/mocks/data/offboardingData';
import { mockedOnboardings } from '@/mocks/data/onboardingData';
import { EmployeeSource } from '@/types/employees';

// Padding beyond the named fixtures above so the org has enough employees to
// exercise infinite-scroll pagination (e.g. the dynamic-group live preview)
// locally, and so any mock "N matches" total stays <= the org's actual
// employee count instead of exceeding it.
const BULK_MOCK_EMPLOYEE_COUNT = 50;

const bulkMockEmployees = Array.from(
  { length: BULK_MOCK_EMPLOYEE_COUNT },
  (_, index) => ({
    id: `bulk-mock-employee-${index + 1}`,
    email: `bulk.mock.${index + 1}@example.com`,
    first_name: 'Employee',
    last_name: `${index + 1}`,
    job_title: 'Employee',
    created_at: '2023-05-22',
    updated_at: '2023-05-22',
    status: EmployeeStatusEnum.Active,
    source: EmployeeSource.Manual,
  })
);

// Initial employee data
const initialEmployees = [
  simpleEmployee,
  storefrontEmployee1,
  storefrontEmployee2,
  // Offboarding scenario fixtures (excluding simpleEmployee, already above).
  ...offboardingScenarios
    .map(s => s.employee)
    .filter(e => e.id !== simpleEmployee.id),
  ...bulkMockEmployees,
  ...pendingMockEmployees,
];

// Mutable array for stateful employee management
// This is shared with organizationHandlers POST endpoint for consistency
// NOTE: This array can be mutated by POST requests and will persist across
// stories in the same browser session. Reset automatically via Storybook loaders.
export const mockOrganizationEmployees = [...initialEmployees];

// Reset function for test isolation (called automatically before each story)
export const resetMockEmployees = () => {
  mockOrganizationEmployees.length = 0;
  mockOrganizationEmployees.push(...initialEmployees);
};

export const employeeHandlers = [
  // Powers the champion on-leave pre-check (ithEmployeeLeave flag) in both
  // the Application Settings page and the Configure Applications flow's
  // Save button -- no mock employee is ever on leave, so this always comes
  // back empty rather than 422ing when the flag is on locally.
  http.post(
    '*/v1/organizations/:organizationId/employees/leaves/bulk',
    async () => {
      await delay(200);
      return HttpResponse.json({ employee_leaves: {} }, { status: 200 });
    }
  ),
  // Specific `/employees/<literal>` routes below must stay BEFORE the
  // `/employees/:employeeId` handlers so the literal isn't read as an id.
  http.get(
    '*/v1/organizations/:organizationId/employees/active_xboardings',
    async () => {
      await delay();
      return HttpResponse.json(
        {
          onboarding_employee_ids: mockedOnboardings.map(o => o.employee.id),
          offboarding_employee_ids: mockedOffboardings.map(o => o.employee.id),
        },
        { status: 200 }
      );
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/employees/email/:email',
    async ({ params }) => {
      const email = decodeURIComponent(String(params.email)).toLowerCase();
      const employee = mockOrganizationEmployees.find(
        e => e.email?.toLowerCase() === email
      );
      await delay();
      return employee
        ? HttpResponse.json(employee, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/employees/employee_application_accounts/search',
    async ({ request, params }) => {
      const { employee_ids } = (await request.json()) as {
        employee_ids: string[];
      };
      const employee_application_accounts_mapping = Object.fromEntries(
        employee_ids.map(employeeId => [
          employeeId,
          employeeIdsWithApps.has(employeeId)
            ? createEmployeeApplicationAccounts(
                String(params.organizationId),
                employeeId
              )
            : [],
        ])
      );
      await delay();
      return HttpResponse.json(
        { employee_application_accounts_mapping },
        { status: 200 }
      );
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/employees/send_welcome_emails',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  // Applies the status change in memory so the People page's Active/Inactive
  // tabs reflect it on the next fetch.
  http.patch(
    '*/v1/organizations/:organizationId/employees/status',
    async ({ request }) => {
      const { employees } = (await request.json()) as {
        employees: { id: string; status: EmployeeStatusEnum }[];
      };
      employees.forEach(({ id, status }) => {
        const index = mockOrganizationEmployees.findIndex(e => e.id === id);
        if (index !== -1) {
          mockOrganizationEmployees[index] = {
            ...mockOrganizationEmployees[index],
            status,
          };
        }
      });
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  http.patch('*/v1/organizations/:organizationId/employees', async () => {
    await delay();
    return new HttpResponse(null, { status: 200 });
  }),
  http.patch('*/v2/organizations/:organizationId/employees', async () => {
    await delay();
    return HttpResponse.json(
      { workflow_id: 'mock-bulk-update-employees-workflow-id' },
      { status: 200 }
    );
  }),
  http.post(
    '*/v1/organizations/:organizationId/desktop_app/employees_app_instructions',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/desktop_app/mdm_agent_install_instructions',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/desktop_app/unenroll_device',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  // Get list of employees for organization
  // Honors `status` query param so the People page's Active/Inactive tabs render
  // distinct results (the SDK appends one `status` param per requested status).
  http.get(
    '*/v1/organizations/:organizationId/employees',
    async ({ request }) => {
      const url = new URL(request.url);
      const statuses = url.searchParams
        .getAll('status')
        .map(s => s.toUpperCase());
      const term = url.searchParams.get('term')?.toLowerCase();
      const limitParam = url.searchParams.get('limit');
      const offsetParam = url.searchParams.get('offset');

      let results = statuses.length
        ? mockOrganizationEmployees.filter(emp =>
            statuses.includes(String(emp.status).toUpperCase())
          )
        : mockOrganizationEmployees;

      if (term) {
        results = results.filter(emp =>
          `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(term)
        );
      }

      const total = results.length;
      if (limitParam) {
        const limit = parseInt(limitParam, 10);
        const offset = offsetParam ? parseInt(offsetParam, 10) : 0;
        results = results.slice(offset, offset + limit);
      }

      // `?pendingDelayMs=3000` on the page URL slows the pending employees list
      // so the Alerts tile's loading state can be seen, as it would be with a
      // very large list.
      const pendingDelayMs = Number(
        new URLSearchParams(window.location.search).get('pendingDelayMs')
      );
      await delay(
        statuses.includes(EmployeeStatusEnum.Unknown.toUpperCase()) &&
          pendingDelayMs > 0
          ? pendingDelayMs
          : undefined
      );
      return HttpResponse.json({ results, total }, { status: 200 });
    }
  ),
  // Resolves a set of employee ids in one call (e.g. a scheduled task's
  // recipients); ids that match nobody are simply absent from the mapping.
  http.post(
    '*/v1/organizations/:organizationId/employees/bulk',
    async ({ request }) => {
      const { employee_ids } = (await request.json()) as {
        employee_ids: string[];
      };
      const employees_mapping = Object.fromEntries(
        mockOrganizationEmployees
          .filter(employee => employee_ids.includes(employee.id))
          .map(employee => [employee.id, employee])
      );
      await delay();
      return HttpResponse.json({ employees_mapping }, { status: 200 });
    }
  ),
  // Get current user's employee record (must come before wildcard :employeeId)
  http.get('*/v1/organizations/:id/employees/me', async () => {
    await delay();
    return HttpResponse.json(simpleEmployee, { status: 200 });
  }),
  // Directory listing (must come before wildcard :employeeId).
  // Honors the `status` query param so callers like the offboarding
  // ReassignTasks assignee picker (which filters to Active) don't get
  // inactive employees mixed in.
  http.get(
    '*/v1/organizations/:organizationId/employees/directory',
    async ({ request }) => {
      const url = new URL(request.url);
      const statuses = url.searchParams.getAll('status');

      const results = mockOrganizationEmployees.filter(emp =>
        statuses.length ? statuses.includes(emp.status) : true
      );

      await delay();
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
  // Get individual employee by ID (used for pre-selection)
  // IMPORTANT: This must come AFTER /employees/me to avoid shadowing
  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId',
    async ({ params }) => {
      await delay();
      const { employeeId } = params;

      // Return the appropriate employee based on ID
      const employeeMap: Record<string, typeof storefrontEmployee1> = {
        'storefront-emp-1': storefrontEmployee1,
        'storefront-emp-2': storefrontEmployee2,
        ...offboardingScenarioEmployeesById,
        ...Object.fromEntries(pendingMockEmployees.map(e => [e.id, e])),
      };

      const employee = employeeMap[employeeId as string] || simpleEmployee;
      return HttpResponse.json(employee, { status: 200 });
    }
  ),
  http.get('*/v1/employees/:id', async () => {
    await delay();
    return HttpResponse.json(simpleEmployee, { status: 200 });
  }),
  http.get('*/v1/organizations/:id/employees/:id', async () => {
    await delay();
    return HttpResponse.json(simpleEmployee, { status: 200 });
  }),
  http.get(
    '*/v1/organizations/:id/employees/:employeeId/devices',
    async ({ request }) => {
      const url = new URL(request.url);
      if (url.searchParams.get('employeeId') === simpleEmployee.id) {
        await delay();
        return HttpResponse.json(singleDevice, { status: 200 });
      } else {
        await delay();
        return HttpResponse.json([], { status: 200 });
      }
    }
  ),
  http.patch('*/v1/organizations/:id/employees/:id', async () => {
    await delay();
    return new HttpResponse(null, { status: 200 });
  }),
  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId/groups',
    async ({ params }) => {
      const { employeeId } = params;
      // Offboarding scenario employees follow their capabilities (no apps =
      // no groups); every other employee gets groups so the profile edit page
      // and its group change confirmation can be exercised.
      const isScenarioWithoutApps =
        (employeeId as string) in offboardingScenarioEmployeesById &&
        !employeeIdsWithApps.has(employeeId as string);
      const groups = isScenarioWithoutApps ? [] : employeeGroups;
      await delay();
      return HttpResponse.json(groups, { status: 200 });
    }
  ),
  // Returns account rows for the apps an employee should appear to "have access
  // to" — used by the offboarding Applications step to filter the group app
  // list down to apps the employee actually holds. Application ids here must
  // match those in `simpleGroupApplicationList` (Zoom, Teams) so the apps
  // actually render in the step.
  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId/employee_application_accounts',
    async ({ params }) => {
      const { organizationId, employeeId } = params;
      const results = employeeIdsWithApps.has(employeeId as string)
        ? createEmployeeApplicationAccounts(
            String(organizationId),
            String(employeeId)
          )
        : [];
      await delay();
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
  // Bulk-grants application access to a set of employees — used by
  // AssignApplicationToGroupModal and the Configure Applications page's
  // access-type step. No response body is read by the caller, just a 200.
  http.post(
    '*/v1/organizations/:organizationId/employees/employee_application_accounts',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  // Accounts per application: every employee that "has apps" holds one, plus one
  // for an employee outside the user's divisions (not in the employees list) so
  // the Users tab can be seen hiding it.
  http.get(
    '*/v1/organizations/:organizationId/applications/:applicationKey/employee_application_accounts',
    async ({ request, params }) => {
      const url = new URL(request.url);
      const limit = parseInt(url.searchParams.get('limit') ?? '', 10);
      const offset = parseInt(url.searchParams.get('offset') ?? '0', 10);
      const accounts = [...employeeIdsWithApps, 'restricted-employee-id'].map(
        employeeId => ({
          ...createEmployeeApplicationAccounts(
            String(params.organizationId),
            employeeId
          )[0],
          id: `eaa-${params.applicationKey}-${employeeId}`,
          application_id: String(params.applicationKey),
        })
      );
      const results = Number.isNaN(limit)
        ? accounts.slice(offset)
        : accounts.slice(offset, offset + limit);
      await delay();
      return HttpResponse.json(
        { results, total: accounts.length },
        { status: 200 }
      );
    }
  ),
  // Note: `*/v1/organizations/:organizationId/assets` is handled by
  // assetHandlers.ts so it can serve the offboarding Devices step a returnable
  // asset for the requested employee.

  // Empty change log is sufficient for the profile to render — no historical
  // status transitions surface in mock mode.
  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId/status-change-history',
    async () => {
      await delay();
      return HttpResponse.json({ results: [], total: 0 }, { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/employees/:employeeId/employee_application_accounts',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  http.delete(
    '*/v1/organizations/:organizationId/employees/employee_application_accounts',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  http.delete(
    '*/v1/organizations/:organizationId/employees/:employeeId/employee_application_accounts',
    async () => {
      await delay();
      return new HttpResponse(null, { status: 200 });
    }
  ),
  // Employee leaves are kept in memory (`mockEmployeeLeaves`) so a leave that
  // was created, edited, ended or canceled shows up on the next fetch.
  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId/leaves',
    async ({ params }) => {
      const results = mockEmployeeLeaves.filter(
        leave => leave.employee_id === params.employeeId
      );
      await delay();
      return HttpResponse.json(
        { results, total: results.length },
        { status: 200 }
      );
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/employees/:employeeId/leaves',
    async ({ request, params }) => {
      const body = (await request.json()) as CreateEmployeeLeaveRequest;
      const now = new Date().toISOString();
      const leave: EmployeeLeaveResponseDto = {
        id: `mock-leave-${mockEmployeeLeaves.length + 1}`,
        employee_id: String(params.employeeId),
        start_date: body.start_date,
        return_date: body.return_date,
        status:
          body.start_date > now.slice(0, 10)
            ? EmployeeLeaveStatusEnum.Scheduled
            : EmployeeLeaveStatusEnum.InProgress,
        default_assignee_id: body.default_assignee_id,
        forwardee_employee_id: body.forwardee_employee_id,
        ooo_auto_responder_enabled: body.ooo_auto_responder_enabled ?? false,
        ooo_auto_responder_message: body.ooo_auto_responder_message,
        champion_reassignments: (body.champion_reassignments ?? []).map(
          (reassignment, index) => ({
            ...reassignment,
            id: `mock-leave-${mockEmployeeLeaves.length + 1}-reassignment-${index + 1}`,
            employee_leave_id: `mock-leave-${mockEmployeeLeaves.length + 1}`,
            created_at: now,
          })
        ),
        created_at: now,
        updated_at: now,
      };
      mockEmployeeLeaves.push(leave);
      await delay();
      return HttpResponse.json(leave, { status: 201 });
    }
  ),
  http.get(
    '*/v1/organizations/:organizationId/employees/:employeeId/leaves/:leaveId',
    async ({ params }) => {
      const leave = mockEmployeeLeaves.find(l => l.id === params.leaveId);
      await delay();
      return leave
        ? HttpResponse.json(leave, { status: 200 })
        : new HttpResponse(null, { status: 404 });
    }
  ),
  http.patch(
    '*/v1/organizations/:organizationId/employees/:employeeId/leaves/:leaveId',
    async ({ request, params }) => {
      const index = mockEmployeeLeaves.findIndex(l => l.id === params.leaveId);
      if (index === -1) {
        return new HttpResponse(null, { status: 404 });
      }
      const { start_date, return_date } = (await request.json()) as {
        start_date: string;
        return_date: string | null;
      };
      mockEmployeeLeaves[index] = {
        ...mockEmployeeLeaves[index],
        start_date,
        return_date: return_date ?? undefined,
        updated_at: new Date().toISOString(),
      };
      await delay();
      return HttpResponse.json(mockEmployeeLeaves[index], { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/employees/:employeeId/leaves/:leaveId/end-early',
    async ({ params }) => {
      const index = mockEmployeeLeaves.findIndex(l => l.id === params.leaveId);
      if (index === -1) {
        return new HttpResponse(null, { status: 404 });
      }
      const now = new Date().toISOString();
      mockEmployeeLeaves[index] = {
        ...mockEmployeeLeaves[index],
        return_date: now.slice(0, 10),
        status: EmployeeLeaveStatusEnum.Completed,
        updated_at: now,
      };
      await delay();
      return HttpResponse.json(mockEmployeeLeaves[index], { status: 200 });
    }
  ),
  http.post(
    '*/v1/organizations/:organizationId/employees/:employeeId/leaves/:leaveId/cancel',
    async ({ params }) => {
      const index = mockEmployeeLeaves.findIndex(l => l.id === params.leaveId);
      if (index === -1) {
        return new HttpResponse(null, { status: 404 });
      }
      const now = new Date().toISOString();
      mockEmployeeLeaves[index] = {
        ...mockEmployeeLeaves[index],
        status: EmployeeLeaveStatusEnum.Canceled,
        canceled_at: now,
        updated_at: now,
      };
      await delay();
      return HttpResponse.json(mockEmployeeLeaves[index], { status: 200 });
    }
  ),
];
